import { useContext, useEffect, useRef } from "react";
import useConfig from "../../demos/hooks/useConfig";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import { useRoleProfileStore, type ToolboxKey } from "./roleProfileStore";

interface ProfileResponse {
  role: string;
  profile: {
    label: string;
    config: Record<string, unknown>;
    toolbox: Partial<Record<ToolboxKey, boolean>>;
  } | null;
}

/**
 * Applies the signed-in person's profile to this viewer.
 *
 * The site publishes one configuration; a profile is what one role gets on top
 * of it — fewer menus for someone who only consults plans, the annotation
 * tools for someone who marks them up, everything for the administrator. It is
 * fetched at sign-in rather than baked into the build, so changing what a role
 * sees takes a reload instead of a redeployment.
 *
 * Nothing here is a security boundary: what the profile hides is what a person
 * is meant to work with, and every endpoint behind those entries makes its own
 * checks.
 */
export function useRoleProfile() {
  const { config, updateConfig } = useConfig();
  const { user } = useContext(CadviewerContext);
  const setProfile = useRoleProfileStore((state) => state.setProfile);
  const clear = useRoleProfileStore((state) => state.clear);

  const token = (user as { token?: string } | null)?.token;
  /** The published values a profile overwrote, so signing out puts them back. */
  const published = useRef<Record<string, unknown> | null>(null);
  const appliedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      // Signed out: undo whatever the last profile changed, once.
      if (published.current) {
        updateConfig(published.current as any);
        published.current = null;
      }
      appliedFor.current = null;
      clear();
      return;
    }
    if (appliedFor.current === token) return;
    appliedFor.current = token;

    let cancelled = false;
    const base = (config.analyticsApiUrl || config.ServerBackEndUrl || "").replace(/\/+$/, "");
    if (!base) return;

    (async () => {
      try {
        const response = await fetch(`${base}/api/v1/tenant/frontend/profile`, {
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data: ProfileResponse = await response.json();
        if (cancelled || !data?.profile) return;

        const overrides = data.profile.config ?? {};
        // Remember what these keys were before touching them: a profile is a
        // view of the site, not a change to it.
        const previous: Record<string, unknown> = {};
        for (const key of Object.keys(overrides)) {
          previous[key] = (config as unknown as Record<string, unknown>)[key];
        }
        published.current = previous;

        if (Object.keys(overrides).length) updateConfig(overrides as any);
        setProfile({
          role: data.role,
          label: data.profile.label,
          toolbox: data.profile.toolbox,
        });
      } catch {
        // A site whose API cannot be reached keeps the configuration it was
        // published with — which is how every deployment behaved before.
      }
    })();

    return () => {
      cancelled = true;
    };
    // `config` is intentionally not a dependency: it changes as a result of
    // this effect, and re-running on that would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, clear, setProfile, updateConfig]);
}
